import React, { useState, useEffect } from 'react';
import { AlertCircle, CheckCircle, Clock, Euro, Plus, Trash2, Edit, CheckSquare, Square, Download, Shield, ArrowLeft, Upload, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import jsPDF from 'jspdf';
import baserowApi from '../services/baserowApi';
import { DamageReport, DamageReportForm, TodoItem, HistoryItem } from '../types';

// Kategorien für Projekte (wird in der Beschreibung als Zeile "Kategorie: <Wert>" gespeichert)
const CATEGORY_OPTIONS = [
  'Schaden',
  'Termin',
  'Service & Inspektion',
] as const;
type ProjectCategory = typeof CATEGORY_OPTIONS[number];

const CATEGORY_LABEL_COLORS: Record<ProjectCategory, string> = {
  'Schaden': 'bg-red-100 text-red-700 border-red-200',
  'Termin': 'bg-[#add015] bg-opacity-20 text-black border-[#add015]',
  'Service & Inspektion': 'bg-gray-100 text-gray-700 border-gray-300',
};

function extractCategoryFromDescription(description: string): ProjectCategory | null {
  if (!description) return null;
  const match = description.match(/^Kategorie:\s*(.+)$/m);
  if (match) {
    const value = match[1].trim();
    if ((CATEGORY_OPTIONS as readonly string[]).includes(value)) {
      return value as ProjectCategory;
    }
  }
  return null;
}

function setCategoryInDescription(description: string, category: ProjectCategory): string {
  if (!description) {
    return `Kategorie: ${category}`;
  }
  if (/^Kategorie:\s*.+$/m.test(description)) {
    return description.replace(/^Kategorie:\s*.+$/m, `Kategorie: ${category}`);
  }
  return `Kategorie: ${category}\n${description}`;
}

const getStatusIcon = (status: any) => {
  const statusValue = typeof status === 'object' ? status.value : status;
  switch (statusValue) {
    case 'Gemeldet':
      return <AlertCircle className="w-4 h-4 text-yellow-500" />;
    case 'in Bearbeitung':
      return <Clock className="w-4 h-4 text-[#add015]" />;
    case 'erledigt':
      return <CheckCircle className="w-4 h-4 text-[#add015]" />;
    default:
      return <AlertCircle className="w-4 h-4 text-gray-500" />;
  }
};

const getStatusText = (status: any) => {
  const statusValue = typeof status === 'object' ? status.value : status;
  return statusValue || 'Unbekannt';
};

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('de-DE');
};

// Hilfsfunktionen für JSON-Parsing
const parseTodos = (todosString: string): TodoItem[] => {
  try {
    return JSON.parse(todosString || '[]');
  } catch {
    return [];
  }
};

const parseHistory = (historyString: string): HistoryItem[] => {
  try {
    return JSON.parse(historyString || '[]');
  } catch {
    return [];
  }
};

interface SchadenmanagementProps {
  darkMode?: boolean;
  vehicles?: any[];
  vehicleDocuments?: any;
  setVehicleDocuments?: any;
}

const Schadenmanagement: React.FC<SchadenmanagementProps> = ({ 
  darkMode = false, 
  vehicles = [], 
  vehicleDocuments = {}, 
  setVehicleDocuments = () => {} 
}) => {
  // Auth removed - all users have access
  const [damageReports, setDamageReports] = useState<DamageReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [formCategory, setFormCategory] = useState<ProjectCategory>('Schaden');
  const [form, setForm] = useState<DamageReportForm>({
    title: '',
    carpool: vehicles.length > 0 ? [vehicles[0].id] : [],
    description: '',
    status: 5382, // Gemeldet
    estimated_costs: '',
    real_costs: '',
    documents: [],
    todos: '[]',
    history: '[]',
    notes: ''
  });
  const [editId, setEditId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<DamageReportForm | null>(null);
  const [editCategory, setEditCategory] = useState<ProjectCategory>('Schaden');
  const [editTodos, setEditTodos] = useState<TodoItem[]>([]);
  const [editHistory, setEditHistory] = useState<HistoryItem[]>([]);
  const [newTodoText, setNewTodoText] = useState('');
  const [newHistoryText, setNewHistoryText] = useState('');
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [editImages, setEditImages] = useState<File[]>([]);
  const [formTodos, setFormTodos] = useState<TodoItem[]>([]);
  const [formHistory, setFormHistory] = useState<HistoryItem[]>([]);
  const [newFormTodoText, setNewFormTodoText] = useState('');
  const [newFormHistoryText, setNewFormHistoryText] = useState('');
  
  // States für Dokument-Upload
  const [editDocuments, setEditDocuments] = useState<any[]>([]);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  
  // States für Dokument-Upload im "Neues Projekt" Formular
  const [formDocuments, setFormDocuments] = useState<any[]>([]);
  const [formUploadingDocument, setFormUploadingDocument] = useState(false);
  const [formSelectedFile, setFormSelectedFile] = useState<File | null>(null);
  
  // Filter- und Such-State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [vehicleFilter, setVehicleFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortBy, setSortBy] = useState<string>('date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Lade Schadensmeldungen beim Komponenten-Mount
  useEffect(() => {
    loadDamageReports();
  }, []);

  // Aktualisiere carpool im Formular, wenn sich die Fahrzeuge ändern
  useEffect(() => {
    if (vehicles.length > 0 && form.carpool.length === 0) {
      setForm(prev => ({
        ...prev,
        carpool: [vehicles[0].id]
      }));
    }
  }, [vehicles, form.carpool.length]);

  const loadDamageReports = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await baserowApi.getDamageReports();
      setDamageReports(response.results);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Laden der Schadensmeldungen');
      console.error('Fehler beim Laden der Schadensmeldungen:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    // Standardmäßig das erste verfügbare Fahrzeug auswählen
    const defaultCarpool = vehicles.length > 0 ? [vehicles[0].id] : [];
    setForm(prev => ({
      ...prev,
      carpool: defaultCarpool
    }));
    setShowForm(true);
    setFormCategory('Schaden');
    // Reset der Aufgaben und Historie für neue Schadensmeldung
    setFormTodos([]);
    setFormHistory([]);
    setNewFormTodoText('');
    setNewFormHistoryText('');
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm(prev => ({
      ...prev,
      [name]: name === 'carpool' ? (value ? [parseInt(value)] : []) : value
    }));
  };

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setUploadedImages(prev => [...prev, ...files]);
  };

  const handleEditImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setEditImages(prev => [...prev, ...files]);
  };

  const removeImage = (index: number, isEdit: boolean = false) => {
    if (isEdit) {
      setEditImages(prev => prev.filter((_, i) => i !== index));
    } else {
      setUploadedImages(prev => prev.filter((_, i) => i !== index));
    }
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      
      // Nur die Felder senden, die in der API-Dokumentation definiert sind
      const formData: any = {
        title: form.title,
        description: form.description,
        status: form.status, // Status-ID (5382=Gemeldet, 5383=in Bearbeitung, 5384=erledigt)
        estimated_costs: form.estimated_costs || "0.00",
        real_costs: form.real_costs || "0.00"
      };

      // Füge Aufgaben und Historie zur Beschreibung hinzu, da die API diese Felder nicht direkt unterstützt
      let enhancedDescription = form.description;
      
      if (formTodos.length > 0) {
        enhancedDescription += '\n\n**Aufgaben:**\n' + formTodos.map(todo => 
          `- [${todo.done ? 'x' : ' '}] ${todo.text}`
        ).join('\n');
      }
      
      if (formHistory.length > 0) {
        enhancedDescription += '\n\n**Historie:**\n' + formHistory.map(entry => 
          `- ${entry.date}: ${entry.text}`
        ).join('\n');
      }
      
      formData.description = setCategoryInDescription(enhancedDescription, formCategory);

      // Nur carpool hinzufügen, wenn ein Fahrzeug ausgewählt ist
      if (form.carpool && form.carpool.length > 0) {
        formData.carpool = form.carpool;
      }

      // Nur documents hinzufügen, wenn Dokumente vorhanden sind
      if (form.documents && form.documents.length > 0) {
        formData.documents = form.documents;
      }
      
      await baserowApi.createDamageReport(formData);
      await loadDamageReports(); // Neu laden
      setShowForm(false);
      setForm({
        title: '',
        carpool: vehicles.length > 0 ? [vehicles[0].id] : [],
        description: '',
        status: 5382,
        estimated_costs: '',
        real_costs: '',
        documents: [],
        todos: '[]',
        history: '[]',
        notes: ''
      });
      setUploadedImages([]);
      setFormTodos([]);
      setFormHistory([]);
      setNewFormTodoText('');
      setNewFormHistoryText('');
      setFormCategory('Schaden');
      // Reset der Dokument-Upload-States
      setFormDocuments([]);
      setFormSelectedFile(null);
      setFormUploadingDocument(false);
    } catch (err: any) {
      setError(err.message || 'Fehler beim Erstellen der Schadensmeldung');
      console.error('Fehler beim Erstellen der Schadensmeldung:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Möchten Sie diese Schadensmeldung wirklich löschen?')) {
      return;
    }
    try {
      setLoading(true);
      await baserowApi.deleteDamageReport(id);
      await loadDamageReports(); // Neu laden
    } catch (err: any) {
      setError(err.message || 'Fehler beim Löschen der Schadensmeldung');
      console.error('Fehler beim Löschen der Schadensmeldung:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (report: DamageReport) => {
    setEditId(report.id);
    setEditForm({
      title: report.title,
      carpool: report.carpool.map(c => c.id),
      description: report.description,
      status: report.status.id,
      estimated_costs: report.estimated_costs,
      real_costs: report.real_costs,
      documents: report.documents.map(d => d.id),
      todos: report.todos || '[]',
      history: report.history || '[]',
      notes: report.notes || ''
    });
    setEditTodos(parseTodos(report.todos || '[]'));
    setEditHistory(parseHistory(report.history || '[]'));
    setEditImages([]);
    const cat = extractCategoryFromDescription(report.description);
    setEditCategory(cat || 'Schaden');
    
    // Lade verknüpfte Dokumente für diesen Fall
    try {
      if (report.documents && report.documents.length > 0) {
        const documentPromises = report.documents.map(doc => 
          baserowApi.getDocument(doc.id).catch(err => {
            console.error(`Fehler beim Laden von Dokument ${doc.id}:`, err);
            return null;
          })
        );
        const documents = await Promise.all(documentPromises);
        const validDocuments = documents.filter(doc => doc !== null);
        setEditDocuments(validDocuments);
      } else {
        setEditDocuments([]);
      }
    } catch (error) {
      console.error('Fehler beim Laden der Dokumente:', error);
      setEditDocuments([]);
    }
  };

  const handleEditChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setEditForm(prev => prev ? {
      ...prev,
      [name]: name === 'carpool' ? [parseInt(value)] : value
    } : null);
  };

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editForm || !editId) return;
    
    try {
      setLoading(true);
      
      // Nur die Felder senden, die in der API-Dokumentation definiert sind
      const updatedForm: any = {
        title: editForm.title,
        description: setCategoryInDescription(editForm.description, editCategory),
        status: editForm.status,
        estimated_costs: editForm.estimated_costs || "0.00",
        real_costs: editForm.real_costs || "0.00"
      };

      // Nur carpool hinzufügen, wenn ein Fahrzeug ausgewählt ist
      if (editForm.carpool && editForm.carpool.length > 0) {
        updatedForm.carpool = editForm.carpool;
      }

      // Nur documents hinzufügen, wenn Dokumente vorhanden sind
      if (editForm.documents && editForm.documents.length > 0) {
        updatedForm.documents = editForm.documents;
      }
      
      await baserowApi.updateDamageReport(editId, updatedForm);
      await loadDamageReports(); // Neu laden
      setEditId(null);
      setEditForm(null);
      setEditTodos([]);
      setEditHistory([]);
      setEditImages([]);
      setEditCategory('Schaden');
    } catch (err: any) {
      setError(err.message || 'Fehler beim Aktualisieren der Schadensmeldung');
      console.error('Fehler beim Aktualisieren der Schadensmeldung:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditCancel = () => {
    setEditId(null);
    setEditForm(null);
    setEditTodos([]);
    setEditHistory([]);
    setEditImages([]);
    setEditDocuments([]);
    setSelectedFile(null);
  };

  // Dokument-Upload-Funktionen
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
    }
  };

  const handleDocumentUpload = async () => {
    if (!selectedFile || !editForm || !editId) return;

    try {
      setUploadingDocument(true);
      
      // 1. Datei hochladen
      const uploadResponse = await baserowApi.uploadFile(selectedFile);
      
      // 2. Dokument in der Tabelle erstellen
      const documentData = {
        title: selectedFile.name,
        description: `Dokument für Fall: ${editForm.title}`,
        files: [uploadResponse.name],
        active: true,
        content_type: selectedFile.type || 'application/octet-stream',
        type: 'other',
        carpool: editForm.carpool
      };
      
      const createdDocument = await baserowApi.createDocument(documentData);
      
      // 3. Damage Report mit neuem Dokument verknüpfen
      const updatedDocuments = [...(editForm.documents || []), createdDocument.id];
      await baserowApi.updateDamageReport(editId, {
        documents: updatedDocuments
      });
      
      // 4. UI aktualisieren
      setEditDocuments(prev => [...prev, createdDocument]);
      setEditForm(prev => prev ? {
        ...prev,
        documents: updatedDocuments
      } : null);
      
      setSelectedFile(null);
      alert('Dokument erfolgreich hochgeladen!');
      
    } catch (error: any) {
      console.error('Fehler beim Hochladen des Dokuments:', error);
      alert('Fehler beim Hochladen: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDocumentDelete = async (documentId: number) => {
    if (!editForm || !editId) return;
    
    const confirmed = window.confirm('Möchten Sie dieses Dokument wirklich löschen?');
    if (!confirmed) return;

    try {
      // 1. Dokument aus Damage Report entfernen
      const updatedDocuments = editForm.documents?.filter(id => id !== documentId) || [];
      await baserowApi.updateDamageReport(editId, {
        documents: updatedDocuments
      });
      
      // 2. Dokument archivieren/löschen
      await baserowApi.deleteDocument(documentId);
      
      // 3. UI aktualisieren
      setEditDocuments(prev => prev.filter(doc => doc.id !== documentId));
      setEditForm(prev => prev ? {
        ...prev,
        documents: updatedDocuments
      } : null);
      
      alert('Dokument erfolgreich gelöscht!');
      
    } catch (error: any) {
      console.error('Fehler beim Löschen des Dokuments:', error);
      alert('Fehler beim Löschen: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  // Dokument-Upload-Funktionen für "Neues Projekt" Formular
  const handleFormFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormSelectedFile(file);
    }
  };

  const handleFormDocumentUpload = async () => {
    if (!formSelectedFile || !form.carpool || form.carpool.length === 0) return;

    try {
      setFormUploadingDocument(true);
      
      // 1. Datei hochladen
      const uploadResponse = await baserowApi.uploadFile(formSelectedFile);
      
      // 2. Dokument in der Tabelle erstellen
      const documentData = {
        title: formSelectedFile.name,
        description: `Dokument für neues Projekt: ${form.title || 'Unbenannt'}`,
        files: [uploadResponse.name],
        active: true,
        content_type: formSelectedFile.type || 'application/octet-stream',
        type: 'other',
        carpool: form.carpool
      };
      
      const createdDocument = await baserowApi.createDocument(documentData);
      
      // 3. Dokument zur Form-Liste hinzufügen
      setFormDocuments(prev => [...prev, createdDocument]);
      
      // 4. Dokument-ID zum Formular hinzufügen
      setForm(prev => ({
        ...prev,
        documents: [...(prev.documents || []), createdDocument.id]
      }));
      
      setFormSelectedFile(null);
      alert('Dokument erfolgreich hochgeladen!');
      
    } catch (error: any) {
      console.error('Fehler beim Hochladen des Dokuments:', error);
      alert('Fehler beim Hochladen: ' + (error.message || 'Unbekannter Fehler'));
    } finally {
      setFormUploadingDocument(false);
    }
  };

  const handleFormDocumentDelete = async (documentId: number) => {
    const confirmed = window.confirm('Möchten Sie dieses Dokument wirklich löschen?');
    if (!confirmed) return;

    try {
      // 1. Dokument aus Form entfernen
      const updatedDocuments = form.documents?.filter(id => id !== documentId) || [];
      setForm(prev => ({
        ...prev,
        documents: updatedDocuments
      }));
      
      // 2. Dokument archivieren/löschen
      await baserowApi.deleteDocument(documentId);
      
      // 3. UI aktualisieren
      setFormDocuments(prev => prev.filter(doc => doc.id !== documentId));
      
      alert('Dokument erfolgreich gelöscht!');
      
    } catch (error: any) {
      console.error('Fehler beim Löschen des Dokuments:', error);
      alert('Fehler beim Löschen: ' + (error.message || 'Unbekannter Fehler'));
    }
  };

  // ToDo-Funktionen
  // Aufgaben-Funktionen für das Erstellungsformular
  const addFormTodo = () => {
    if (newFormTodoText.trim()) {
      setFormTodos(prev => [...prev, { text: newFormTodoText.trim(), done: false }]);
      setNewFormTodoText('');
    }
  };

  const toggleFormTodo = (index: number) => {
    setFormTodos(prev => prev.map((todo, i) => 
      i === index ? { ...todo, done: !todo.done } : todo
    ));
  };

  const removeFormTodo = (index: number) => {
    setFormTodos(prev => prev.filter((_, i) => i !== index));
  };

  // Aufgaben-Funktionen für das Bearbeitungsformular
  const addTodo = () => {
    if (newTodoText.trim()) {
      setEditTodos(prev => [...prev, { text: newTodoText.trim(), done: false }]);
      setNewTodoText('');
    }
  };

  const toggleTodo = (index: number) => {
    setEditTodos(prev => prev.map((todo, i) => 
      i === index ? { ...todo, done: !todo.done } : todo
    ));
  };

  const removeTodo = (index: number) => {
    setEditTodos(prev => prev.filter((_, i) => i !== index));
  };

  // History-Funktionen für das Erstellungsformular
  const addFormHistoryEntry = () => {
    if (newFormHistoryText.trim()) {
      const newEntry: HistoryItem = {
        date: new Date().toISOString().split('T')[0],
        text: newFormHistoryText.trim()
      };
      setFormHistory(prev => [...prev, newEntry]);
      setNewFormHistoryText('');
    }
  };

  const removeFormHistoryEntry = (index: number) => {
    setFormHistory(prev => prev.filter((_, i) => i !== index));
  };

  // History-Funktionen für das Bearbeitungsformular
  const addHistoryEntry = () => {
    if (newHistoryText.trim()) {
      const newEntry: HistoryItem = {
        date: new Date().toISOString().split('T')[0],
        text: newHistoryText.trim()
      };
      setEditHistory(prev => [...prev, newEntry]);
      setNewHistoryText('');
    }
  };

  const removeHistoryEntry = (index: number) => {
    setEditHistory(prev => prev.filter((_, i) => i !== index));
  };

  // Status-Änderung mit History-Eintrag
  const handleStatusChange = async (newStatus: number) => {
    if (!editForm || !editId) return;
    
    const statusText = baserowApi.getStatusValue(newStatus);
    const newHistoryEntry: HistoryItem = {
      date: new Date().toISOString().split('T')[0],
      text: `Status auf '${statusText}' geändert.`
    };
    
    setEditHistory(prev => [...prev, newHistoryEntry]);
    setEditForm(prev => prev ? { ...prev, status: newStatus } : null);
  };

  // Schnellstatus-Änderung für Schadensmeldungen
  const handleQuickStatusChange = async (reportId: number, newStatus: number) => {
    try {
      const statusText = baserowApi.getStatusValue(newStatus);
      const newHistoryEntry: HistoryItem = {
        date: new Date().toISOString().split('T')[0],
        text: `Status auf '${statusText}' geändert.`
      };
      
      // Aktuelle Schadensmeldung finden
      const report = damageReports.find(r => r.id === reportId);
      if (!report) return;
      
      // Historie aktualisieren
      const currentHistory = parseHistory(report.history || '[]');
      const updatedHistory = [...currentHistory, newHistoryEntry];
      
      // Schadensmeldung aktualisieren
      await baserowApi.updateDamageReport(reportId, {
        status: newStatus,
        history: JSON.stringify(updatedHistory)
      });
      
      // Lokale Daten aktualisieren
      setDamageReports(prev => prev.map(r => 
        r.id === reportId 
          ? { ...r, status: { id: newStatus, value: statusText, color: '' }, history: JSON.stringify(updatedHistory) }
          : r
      ));
      
    } catch (error: any) {
      console.error('Fehler beim Status-Update:', error);
      setError('Fehler beim Aktualisieren des Status');
    }
  };

  // PDF-Export für einen Schaden
  const exportSchadenPdf = (report: DamageReport) => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Schadendokumentation', 10, 15);
    doc.setFontSize(12);
    
    const vehicleInfo = report.carpool.length > 0 ? report.carpool[0].value : 'Unbekanntes Fahrzeug';
    doc.text(`Fahrzeug: ${vehicleInfo}`, 10, 30);
    doc.text(`Titel: ${report.title}`, 10, 38);
    doc.text(`Erstellt am: ${formatDate(report["Erstellt am"])}`, 10, 46);
    doc.text(`Status: ${getStatusText(report.status)}`, 10, 54);
    doc.text(`Beschreibung: ${report.description}`, 10, 62);
    doc.text(`Geschätzte Kosten: ${report.estimated_costs} €`, 10, 70);
    doc.text(`Tatsächliche Kosten: ${report.real_costs || 'Noch nicht bekannt'} €`, 10, 78);
    
    if (report.notes) {
      doc.text('Notizen:', 10, 86);
      doc.text(report.notes, 10, 94);
    }
    
    // ToDos
    const todos = parseTodos(report.todos || '[]');
    if (todos.length > 0) {
      let y = 110;
      doc.text('Aufgaben:', 10, y);
      y += 8;
      todos.forEach((todo) => {
        doc.text(`- [${todo.done ? 'x' : ' '}] ${todo.text}`, 12, y);
        y += 8;
      });
    }
    
    // History
    const history = parseHistory(report.history || '[]');
    if (history.length > 0) {
      let y = 150;
      doc.text('Historie:', 10, y);
      y += 8;
      history.forEach((entry) => {
        doc.text(`- ${entry.date}: ${entry.text}`, 12, y);
        y += 8;
      });
    }
    
    if (report.documents && report.documents.length > 0) {
      doc.text('Dokumente:', 10, 200);
      let y = 208;
      report.documents.forEach((docItem) => {
        doc.text(`- ${docItem.value}`, 12, y);
        y += 8;
      });
    }

    const pdfName = `Schadensmeldung_${report.title.replace(/[^a-zA-Z0-9]/g, '_')}_${formatDate(report["Erstellt am"])}.pdf`;
    doc.save(pdfName);
  };

  // Erweiterte Filterlogik
  const filteredDamageReports = damageReports.filter(report => {
    // Suchfilter
    const matchesSearch = searchTerm === '' || 
      report.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
      report.carpool.some(c => c.value.toLowerCase().includes(searchTerm.toLowerCase()));
    
    // Statusfilter
    const matchesStatus = statusFilter === 'all' || 
      report.status.value === statusFilter;
    // Kategoriefilter (aus Beschreibung extrahiert)
    const reportCategory = extractCategoryFromDescription(report.description);
    
    // Nur Projekte mit gültiger Kategorie anzeigen
    if (!reportCategory) {
      return false;
    }
    
    const matchesCategory = categoryFilter === 'all' || reportCategory === categoryFilter;
    
    // Fahrzeugfilter
    const matchesVehicle = vehicleFilter === 'all' || 
      report.carpool.some(c => c.value === vehicleFilter);
    
    return matchesSearch && matchesStatus && matchesVehicle && matchesCategory;
  });

  // Sortierung
  const sortedDamageReports = [...filteredDamageReports].sort((a, b) => {
    let aValue: any, bValue: any;
    
    switch (sortBy) {
      case 'date':
        aValue = new Date(a["Erstellt am"]);
        bValue = new Date(b["Erstellt am"]);
        break;
      case 'title':
        aValue = a.title.toLowerCase();
        bValue = b.title.toLowerCase();
        break;
      case 'status':
        aValue = a.status.value;
        bValue = b.status.value;
        break;
      case 'costs':
        aValue = parseFloat(a.estimated_costs || '0');
        bValue = parseFloat(b.estimated_costs || '0');
        break;
      default:
        aValue = new Date(a["Erstellt am"]);
        bValue = new Date(b["Erstellt am"]);
    }
    
    if (sortOrder === 'asc') {
      return aValue > bValue ? 1 : -1;
    } else {
      return aValue < bValue ? 1 : -1;
    }
  });

  // Filtere offene und abgeschlossene Schäden
  const openDamages = sortedDamageReports.filter(report => 
    report.status.value !== 'erledigt'
  );
  const completedDamages = sortedDamageReports.filter(report => 
    report.status.value === 'erledigt'
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          <strong className="font-bold">Fehler:</strong>
          <span className="block sm:inline"> {error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 bg-white min-h-screen">
      <div className="mb-6">
        <div className="grid grid-cols-3 items-center">
          <div className="justify-self-start">
            <Link
              to="/"
              className="flex items-center space-x-2 text-gray-600 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              <span>Zurück zum Hub</span>
            </Link>
          </div>
          <div className="justify-self-center">
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">Fahrzeugmanagement&Schäden</h2>
          </div>
          <div className="justify-self-end flex items-center gap-4">
          <button
            className="bg-[#add015] text-[#111] rounded-lg px-4 py-2 font-semibold flex items-center gap-2 hover:bg-[#b8e35a]"
            onClick={handleAddNew}
          >
            <Plus className="w-5 h-5" /> + Neues Projekt
          </button>
          </div>
        </div>
      </div>

      {/* Statistiken */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4">
          <div className="flex items-center">
            <AlertCircle className="w-8 h-8 text-yellow-500" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Gemeldet</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {damageReports.filter(r => r.status.value === 'Gemeldet').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4">
          <div className="flex items-center">
            <Clock className="w-8 h-8 text-[#add015]" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">In Bearbeitung</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {damageReports.filter(r => r.status.value === 'in Bearbeitung').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4">
          <div className="flex items-center">
            <CheckCircle className="w-8 h-8 text-[#add015]" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Erledigt</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {damageReports.filter(r => r.status.value === 'erledigt').length}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4">
          <div className="flex items-center">
            <Euro className="w-8 h-8 text-[#add015]" />
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Gesamtkosten</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {damageReports
                  .filter(r => r.real_costs && parseFloat(r.real_costs) > 0)
                  .reduce((sum, r) => sum + parseFloat(r.real_costs || '0'), 0)
                  .toFixed(2)}€
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Zusätzliche Statistiken */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Durchschnittliche Kosten</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {(() => {
                  const costs = damageReports
                    .filter(r => r.real_costs && parseFloat(r.real_costs) > 0)
                    .map(r => parseFloat(r.real_costs || '0'));
                  return costs.length > 0 
                    ? (costs.reduce((sum, cost) => sum + cost, 0) / costs.length).toFixed(2)
                    : '0.00';
                })()}€
              </p>
            </div>
            <Euro className="w-8 h-8 text-gray-600" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Offene Aufgaben</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {damageReports.reduce((total, report) => {
                  const todos = parseTodos(report.todos || '[]');
                  return total + todos.filter(todo => !todo.done).length;
                }, 0)}
              </p>
            </div>
            <CheckSquare className="w-8 h-8 text-[#add015]" />
          </div>
        </div>
        
        <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-gray-400">Letzte Aktivität</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white">
                {(() => {
                  const latestDate = damageReports
                    .map(r => new Date(r["Erstellt am"]))
                    .sort((a, b) => b.getTime() - a.getTime())[0];
                  return latestDate ? formatDate(latestDate.toISOString()) : 'Keine';
                })()}
              </p>
            </div>
            <Clock className="w-8 h-8 text-gray-600" />
          </div>
        </div>
      </div>

      {/* Filter und Suche */}
      <div className="bg-white dark:bg-[#181c20] rounded-xl shadow-md border border-gray-200 dark:border-[#23272e] p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          {/* Suchfeld */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Suche</label>
            <input
              type="text"
              placeholder="Titel, Beschreibung oder Fahrzeug..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
            />
          </div>
          
          {/* Statusfilter */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Status</label>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
            >
              <option value="all">Alle Status</option>
              <option value="Gemeldet">Gemeldet</option>
              <option value="in Bearbeitung">In Bearbeitung</option>
              <option value="erledigt">Erledigt</option>
            </select>
          </div>
          
          {/* Fahrzeugfilter */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Fahrzeug</label>
            <select
              value={vehicleFilter}
              onChange={(e) => setVehicleFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
            >
              <option value="all">Alle Fahrzeuge</option>
              {Array.from(new Set(damageReports.flatMap(r => r.carpool.map(c => c.value)))).map(vehicle => (
                <option key={vehicle} value={vehicle}>{vehicle}</option>
              ))}
            </select>
          </div>
          
          {/* Kategoriefilter */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Kategorie</label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
            >
              <option value="all">Alle Kategorien</option>
              {CATEGORY_OPTIONS.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>

          {/* Sortierung */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Sortierung</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
            >
              <option value="date">Datum</option>
              <option value="title">Titel</option>
              <option value="status">Status</option>
              <option value="costs">Kosten</option>
            </select>
          </div>
          
          {/* Sortierreihenfolge */}
          <div>
            <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Reihenfolge</label>
            <select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
              className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
            >
              <option value="desc">Absteigend</option>
              <option value="asc">Aufsteigend</option>
            </select>
          </div>
        </div>
        
        {/* Ergebnisse-Anzeige */}
        <div className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          {filteredDamageReports.length} von {damageReports.length} Projekten gefunden
        </div>
      </div>

      {/* Formular für neue Schadensmeldung */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto rounded-xl shadow-xl ${
            'bg-white'
          }`}>
            <form onSubmit={handleFormSubmit} className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Neues Projekt</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Kategorie</label>
                  <select
                    value={formCategory}
                    onChange={(e) => setFormCategory(e.target.value as ProjectCategory)}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                    required
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Titel</label>
                  <input
                    name="title"
                    value={form.title}
                    onChange={handleFormChange}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Fahrzeug</label>
                  <select
                    name="carpool"
                    value={form.carpool[0] || ''}
                    onChange={handleFormChange}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                    required
                  >
                    <option value="">Fahrzeug auswählen</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.license_plate} - {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Beschreibung</label>
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleFormChange}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                    rows={3}
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Geschätzte Kosten (€)</label>
                  <input
                    name="estimated_costs"
                    type="number"
                    step="0.01"
                    value={form.estimated_costs}
                    onChange={handleFormChange}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Tatsächliche Kosten (€)</label>
                  <input
                    name="real_costs"
                    type="number"
                    step="0.01"
                    value={form.real_costs}
                    onChange={handleFormChange}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Notizen</label>
                  <textarea
                    name="notes"
                    value={form.notes}
                    onChange={handleFormChange}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                    rows={2}
                  />
                </div>
                
                {/* Aufgaben-Sektion */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Aufgaben</label>
                  <div className="space-y-2">
                    {formTodos.map((todo, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleFormTodo(index)}
                          className="flex items-center"
                        >
                          {todo.done ? <CheckSquare className="w-4 h-4 text-[#add015]" /> : <Square className="w-4 h-4" />}
                        </button>
                        <span className={todo.done ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}>{todo.text}</span>
                        <button
                          type="button"
                          onClick={() => removeFormTodo(index)}
                          className="ml-auto text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFormTodoText}
                        onChange={(e) => setNewFormTodoText(e.target.value)}
                        placeholder="Neue Aufgabe..."
                        className="flex-1 border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFormTodo())}
                      />
                      <button
                        type="button"
                        onClick={addFormTodo}
                        className="bg-[#add015] text-black px-3 py-2 rounded-lg hover:bg-[#add015] hover:bg-opacity-80"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>

                {/* Historie-Sektion */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Historie</label>
                  <div className="space-y-2">
                    {formHistory.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm text-gray-600 dark:text-gray-400">{entry.date}:</span>
                        <span className="flex-1 text-gray-900 dark:text-white">{entry.text}</span>
                        <button
                          type="button"
                          onClick={() => removeFormHistoryEntry(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newFormHistoryText}
                        onChange={(e) => setNewFormHistoryText(e.target.value)}
                        placeholder="Neuer Historie-Eintrag..."
                        className="flex-1 border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFormHistoryEntry())}
                      />
                      <button
                        type="button"
                        onClick={addFormHistoryEntry}
                        className="bg-[#add015] text-black px-3 py-2 rounded-lg hover:bg-[#add015] hover:bg-opacity-80"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Bilder hochladen</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="w-full border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                  />
                  {uploadedImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {uploadedImages.map((file, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Upload ${index + 1}`}
                            className="w-16 h-16 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dokument-Upload für neues Projekt */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Dokument hochladen</label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      onChange={handleFormFileSelect}
                      className="flex-1 border border-gray-300 rounded-lg p-2 bg-white text-gray-900"
                    />
                    <button
                      type="button"
                      onClick={handleFormDocumentUpload}
                      disabled={!formSelectedFile || formUploadingDocument}
                      className="px-4 py-2 bg-[#add015] text-black rounded-lg hover:bg-[#add015] hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {formUploadingDocument ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                  {formSelectedFile && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Ausgewählt: {formSelectedFile.name}
                    </p>
                  )}
                </div>

                {/* Hochgeladene Dokumente für neues Projekt anzeigen */}
                {formDocuments.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Hochgeladene Dokumente</label>
                    <div className="space-y-2">
                      {formDocuments.map((doc) => (
                        <button
                          key={doc.id}
                          onClick={() => {
                            console.log('Form: Vollständiges Dokument:', doc);
                            const url = doc.files?.[0]?.url || doc.file_url;
                            console.log('Form: Verwendete URL:', url);
                            if (url) {
                              window.open(url, '_blank');
                            } else {
                              console.error('Form: Keine URL gefunden in:', doc);
                              alert(`Download-URL nicht verfügbar.\nDokument-Struktur: ${JSON.stringify(doc, null, 2)}`);
                            }
                          }}
                          className="w-full flex items-center justify-between p-3 border rounded-lg bg-gray-50 border-gray-200 hover:bg-gray-100 transition-colors text-left"
                          title="Dokument öffnen"
                          type="button"
                        >
                          <div className="flex items-center gap-3">
                            <FileText className="w-5 h-5 text-[#add015]" />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{doc.title}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {doc.content_type} • {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFormDocumentDelete(doc.id);
                              }}
                              className="p-1 text-red-500 hover:text-red-600"
                              title="Dokument löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex gap-2">
                <button
                  type="submit"
                  className="bg-[#add015] text-black px-4 py-2 rounded-lg hover:bg-[#add015] hover:bg-opacity-80"
                >
                  Erstellen
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowForm(false);
                    // Reset auch beim Abbrechen
                    setFormDocuments([]);
                    setFormSelectedFile(null);
                    setFormUploadingDocument(false);
                  }}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Offene Projekte */}
      <div className="mb-8">
        {/* Gruppierung nach Kategorie */}
        {(() => {
          const groups: Record<string, typeof openDamages> = {
            'Schaden': [],
            'Termin': [],
            'Service & Inspektion': []
          };
          openDamages.forEach((report) => {
            const cat = extractCategoryFromDescription(report.description);
            if (cat) {
              groups[cat].push(report);
            }
          });
          return (
            <div className="space-y-8">
              {Object.entries(groups).map(([cat, items]) => (
                items.length === 0 ? null : (
                  <div key={cat}>
                    <div className="flex items-center gap-2 mb-3">
                      <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full border ${CATEGORY_LABEL_COLORS[(cat as ProjectCategory)] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>{cat}</span>
                      <span className="text-sm text-gray-500">({items.length})</span>
                    </div>
                    <div className="grid gap-4">
                      {items.map((report) => (
                        <div key={report.id} className={`p-4 rounded-lg border ${'bg-white border-gray-200'}`}>
                          <div className="flex justify-between items-start mb-3">
                            <div className="flex items-center gap-2">
                              {getStatusIcon(report.status)}
                              <span className="font-semibold text-gray-900 dark:text-white">{report.title}</span>
                            </div>
                            <div className="flex gap-2">
                              {report.status.value !== 'erledigt' && (
                                <select
                                  value={report.status.value}
                                  onChange={(e) => {
                                    const newStatus = e.target.value;
                                    const statusId = newStatus === 'Gemeldet' ? 5382 : newStatus === 'in Bearbeitung' ? 5383 : 5384;
                                    handleQuickStatusChange(report.id, statusId);
                                  }}
                                  className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                >
                                  <option value="Gemeldet">Gemeldet</option>
                                  <option value="in Bearbeitung">In Bearbeitung</option>
                                  <option value="erledigt">Erledigt</option>
                                </select>
                              )}
                              <button onClick={() => handleEdit(report)} className="p-1 text-[#add015] hover:text-[#add015] hover:opacity-80" title="Bearbeiten">
                                <Edit className="w-4 h-4" />
                              </button>
                              <button onClick={() => exportSchadenPdf(report)} className="p-1 text-[#add015] hover:text-[#add015] hover:opacity-80" title="PDF exportieren">
                                <Download className="w-4 h-4" />
                              </button>
                              <button onClick={() => handleDelete(report.id)} className="p-1 text-red-500 hover:text-red-600" title="Löschen">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-white">
                            <div>
                              <span className="font-medium">Fahrzeug:</span> {report.carpool.map(c => c.value).join(', ')}
                            </div>
                            <div>
                              <span className="font-medium">Status:</span> {getStatusText(report.status)}
                            </div>
                            <div>
                              <span className="font-medium">Geschätzte Kosten:</span> {report.estimated_costs} €
                            </div>
                            <div>
                              <span className="font-medium">Tatsächliche Kosten:</span> {report.real_costs || 'Noch nicht bekannt'} €
                            </div>
                          </div>
                          <div className="mt-3">
                            <span className="font-medium text-gray-900 dark:text-white">Beschreibung:</span>
                            <p className="text-sm mt-1 text-gray-900 dark:text-white">{report.description}</p>
                          </div>
                          {report.todos && parseTodos(report.todos || '[]').length > 0 && (
                            <div className="mt-3">
                              <span className="font-medium text-gray-900 dark:text-white">Aufgaben:</span>
                              <ul className="text-sm mt-1 space-y-1">
                                {parseTodos(report.todos || '[]').map((todo, index) => (
                                  <li key={index} className="flex items-center gap-2">
                                    {todo.done ? <CheckSquare className="w-4 h-4 text-[#add015]" /> : <Square className="w-4 h-4" />}
                                    <span className={todo.done ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}>{todo.text}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {report.documents && report.documents.length > 0 && (
                            <div className="mt-3">
                              <span className="font-medium text-gray-900 dark:text-white">Dokumente:</span>
                              <div className="text-sm mt-1 space-y-1">
                                {report.documents.map((doc, index) => (
                                  <button
                                    key={index}
                                    onClick={async () => {
                                      try {
                                        console.log('Lade Dokument mit ID:', doc.id);
                                        const fullDoc = await baserowApi.getDocument(doc.id);
                                        console.log('Vollständiges Dokument geladen:', fullDoc);
                                        
                                        const url = fullDoc.files?.[0]?.url || fullDoc.file_url;
                                        console.log('Verwendete URL:', url);
                                        
                                        if (url) {
                                          console.log('Öffne URL in neuem Tab:', url);
                                          window.open(url, '_blank');
                                        } else {
                                          console.error('Keine URL gefunden in:', fullDoc);
                                          alert(`Download-URL nicht verfügbar.\nDokument-Struktur: ${JSON.stringify(fullDoc, null, 2)}`);
                                        }
                                      } catch (error) {
                                        console.error('Fehler beim Öffnen des Dokuments:', error);
                                        alert('Fehler beim Öffnen des Dokuments: ' + (error instanceof Error ? error.message : 'Unbekannter Fehler'));
                                      }
                                    }}
                                    className="w-full flex items-center gap-2 p-2 bg-gray-50 dark:bg-gray-700 rounded hover:bg-gray-100 transition-colors cursor-pointer text-left"
                                    title="Dokument öffnen"
                                  >
                                    <FileText className="w-4 h-4 text-[#add015]" />
                                    <span className="flex-1 text-gray-900 dark:text-white">{doc.value}</span>
                                    <Download className="w-4 h-4 text-[#add015]" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          {report.history && parseHistory(report.history).length > 0 && (
                            <div className="mt-3">
                              <span className="font-medium text-gray-900 dark:text-white">Historie:</span>
                              <ul className="text-sm mt-1 space-y-1">
                                {parseHistory(report.history).slice(-3).map((entry, index) => (
                                  <li key={index} className="text-gray-600 dark:text-gray-400">
                                    {entry.date}: {entry.text}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {report.notes && (
                            <div className="mt-3">
                              <span className="font-medium text-gray-900 dark:text-white">Notizen:</span>
                              <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">{report.notes}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )
              ))}
            </div>
          );
        })()}
      </div>

      {/* Abgeschlossene Schäden */}
      <div>
        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-gray-200">
          Abgeschlossene Projekte ({completedDamages.length})
        </h3>
        <div className="grid gap-4">
          {completedDamages.map((report) => (
            <div
              key={report.id}
              className={`p-4 rounded-lg border ${
                'bg-white border-gray-200'
              }`}
            >
              <div className="flex justify-between items-start mb-3">
                <div className="flex items-center gap-2">
                  {getStatusIcon(report.status)}
                  <span className="font-semibold text-gray-900 dark:text-white">{report.title}</span>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(report)}
                    className="p-1 text-[#add015] hover:text-[#add015] hover:opacity-80"
                    title="Bearbeiten"
                  >
                    <Edit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => exportSchadenPdf(report)}
                    className="p-1 text-[#add015] hover:text-[#add015] hover:opacity-80"
                    title="PDF exportieren"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(report.id)}
                    className="p-1 text-red-500 hover:text-red-600"
                    title="Löschen"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-900 dark:text-white">
                <div>
                  <span className="font-medium">Fahrzeug:</span> {report.carpool.map(c => c.value).join(', ')}
                </div>
                <div>
                  <span className="font-medium">Status:</span> {getStatusText(report.status)}
                </div>
                <div>
                  <span className="font-medium">Geschätzte Kosten:</span> {report.estimated_costs} €
                </div>
                <div>
                  <span className="font-medium">Tatsächliche Kosten:</span> {report.real_costs || 'Noch nicht bekannt'} €
                </div>
              </div>
              
              <div className="mt-3">
                <span className="font-medium text-gray-900 dark:text-white">Beschreibung:</span>
                <p className="text-sm mt-1 text-gray-900 dark:text-white">{report.description}</p>
              </div>

              {/* ToDos anzeigen */}
              {report.todos && parseTodos(report.todos || '[]').length > 0 && (
                <div className="mt-3">
                  <span className="font-medium text-gray-900 dark:text-white">Aufgaben:</span>
                  <ul className="text-sm mt-1 space-y-1">
                    {parseTodos(report.todos || '[]').map((todo, index) => (
                      <li key={index} className="flex items-center gap-2">
                        {todo.done ? <CheckSquare className="w-4 h-4 text-[#add015]" /> : <Square className="w-4 h-4" />}
                        <span className={todo.done ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}>{todo.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* History anzeigen */}
              {report.history && parseHistory(report.history).length > 0 && (
                <div className="mt-3">
                  <span className="font-medium text-gray-900 dark:text-white">Historie:</span>
                  <ul className="text-sm mt-1 space-y-1">
                    {parseHistory(report.history).slice(-3).map((entry, index) => (
                      <li key={index} className="text-gray-600 dark:text-gray-400">
                        {entry.date}: {entry.text}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {report.notes && (
                <div className="mt-3">
                  <span className="font-medium text-gray-900 dark:text-white">Notizen:</span>
                  <p className="text-sm mt-1 text-gray-600 dark:text-gray-400">{report.notes}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Edit Modal */}
      {editId && editForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto rounded-xl shadow-xl ${
            'bg-white'
          }`}>
            <form onSubmit={handleEditSubmit} className="p-6">
              <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Projekt bearbeiten</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Kategorie</label>
                  <select
                    value={editCategory}
                    onChange={(e) => setEditCategory(e.target.value as ProjectCategory)}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    {CATEGORY_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Titel</label>
                  <input
                    name="title"
                    value={editForm.title}
                    onChange={handleEditChange}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Fahrzeug</label>
                  <select
                    name="carpool"
                    value={editForm.carpool[0] || ''}
                    onChange={handleEditChange}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value="">Fahrzeug auswählen</option>
                    {vehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.license_plate} - {vehicle.model}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Status</label>
                  <select
                    name="status"
                    value={editForm.status}
                    onChange={(e) => handleStatusChange(parseInt(e.target.value))}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                  >
                    <option value={5382}>Gemeldet</option>
                    <option value={5383}>In Bearbeitung</option>
                    <option value={5384}>Erledigt</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Geschätzte Kosten (€)</label>
                  <input
                    name="estimated_costs"
                    type="number"
                    step="0.01"
                    value={editForm.estimated_costs}
                    onChange={handleEditChange}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Tatsächliche Kosten (€)</label>
                  <input
                    name="real_costs"
                    type="number"
                    step="0.01"
                    value={editForm.real_costs}
                    onChange={handleEditChange}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Beschreibung</label>
                  <textarea
                    name="description"
                    value={editForm.description}
                    onChange={handleEditChange}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                    rows={3}
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Notizen</label>
                  <textarea
                    name="notes"
                    value={editForm.notes}
                    onChange={handleEditChange}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                    rows={2}
                  />
                </div>

                {/* ToDos */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Aufgaben</label>
                  <div className="space-y-2">
                    {editTodos.map((todo, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => toggleTodo(index)}
                          className="p-1"
                        >
                          {todo.done ? <CheckSquare className="w-4 h-4 text-[#add015]" /> : <Square className="w-4 h-4" />}
                        </button>
                        <span className={todo.done ? 'line-through text-gray-500 dark:text-gray-400' : 'text-gray-900 dark:text-white'}>{todo.text}</span>
                        <button
                          type="button"
                          onClick={() => removeTodo(index)}
                          className="ml-auto text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newTodoText}
                        onChange={(e) => setNewTodoText(e.target.value)}
                        placeholder="Neue Aufgabe..."
                        className={`flex-1 p-2 border rounded ${
                          'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={addTodo}
                        className="px-3 py-2 bg-[#add015] text-black rounded hover:bg-[#add015] hover:bg-opacity-80"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>

                {/* History */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Historie</label>
                  <div className="space-y-2">
                    {editHistory.map((entry, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-sm text-gray-500 dark:text-gray-400">{entry.date}:</span>
                        <span className="flex-1 text-gray-900 dark:text-white">{entry.text}</span>
                        <button
                          type="button"
                          onClick={() => removeHistoryEntry(index)}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newHistoryText}
                        onChange={(e) => setNewHistoryText(e.target.value)}
                        placeholder="Neuer Historie-Eintrag..."
                        className={`flex-1 p-2 border rounded ${
                          'bg-white border-gray-300 text-gray-900'
                        }`}
                      />
                      <button
                        type="button"
                        onClick={addHistoryEntry}
                        className="px-3 py-2 bg-[#add015] text-black rounded hover:bg-[#add015] hover:bg-opacity-80"
                      >
                        Hinzufügen
                      </button>
                    </div>
                  </div>
                </div>

                {/* Bild-Upload */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Bilder hinzufügen</label>
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleEditImageUpload}
                    className={`w-full border rounded-lg p-2 ${
                      'bg-white border-gray-300 text-gray-900'
                    }`}
                  />
                  {editImages.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {editImages.map((file, index) => (
                        <div key={index} className="relative">
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`Upload ${index + 1}`}
                            className="w-16 h-16 object-cover rounded border"
                          />
                          <button
                            type="button"
                            onClick={() => removeImage(index, true)}
                            className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Dokument-Upload */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Dokument hochladen</label>
                  <div className="flex gap-2">
                    <input
                      type="file"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
                      onChange={handleFileSelect}
                      className={`flex-1 border rounded-lg p-2 ${
                        'bg-white border-gray-300 text-gray-900'
                      }`}
                    />
                    <button
                      type="button"
                      onClick={handleDocumentUpload}
                      disabled={!selectedFile || uploadingDocument}
                      className="px-4 py-2 bg-[#add015] text-black rounded-lg hover:bg-[#add015] hover:bg-opacity-80 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Upload className="w-4 h-4" />
                      {uploadingDocument ? 'Uploading...' : 'Upload'}
                    </button>
                  </div>
                  {selectedFile && (
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                      Ausgewählt: {selectedFile.name}
                    </p>
                  )}
                </div>

                {/* Verknüpfte Dokumente anzeigen */}
                {editDocuments.length > 0 && (
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium mb-2 text-gray-900 dark:text-white">Verknüpfte Dokumente</label>
                    <div className="space-y-2">
                      {editDocuments.map((doc) => (
                        <div key={doc.id} className={`flex items-center justify-between p-3 border rounded-lg ${
                          'bg-gray-50 border-gray-200'
                        }`}>
                          <button
                            onClick={() => {
                              console.log('Modal: Vollständiges Dokument:', doc);
                              const url = doc.files?.[0]?.url || doc.file_url;
                              console.log('Modal: Verwendete URL:', url);
                              if (url) {
                                window.open(url, '_blank');
                              } else {
                                console.error('Modal: Keine URL gefunden in:', doc);
                                alert(`Download-URL nicht verfügbar.\nDokument-Struktur: ${JSON.stringify(doc, null, 2)}`);
                              }
                            }}
                            className="flex items-center gap-3 flex-1 text-left hover:bg-gray-100 p-2 rounded transition-colors"
                            title="Dokument öffnen"
                          >
                            <FileText className="w-5 h-5 text-[#add015]" />
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white">{doc.title}</p>
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                {doc.content_type} • {new Date(doc.created_at).toLocaleDateString()}
                              </p>
                            </div>
                          </button>
                          <div className="flex items-center gap-2 ml-2">
                            <button
                              type="button"
                              onClick={() => handleDocumentDelete(doc.id)}
                              className="p-1 text-red-500 hover:text-red-600"
                              title="Dokument löschen"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              <div className="mt-6 flex gap-2">
                <button
                  type="submit"
                  className="bg-[#add015] text-black px-4 py-2 rounded-lg hover:bg-[#add015] hover:bg-opacity-80"
                >
                  Speichern
                </button>
                <button
                  type="button"
                  onClick={handleEditCancel}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300"
                >
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Schadenmanagement; 